import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import CompaniesPage from './pages/CompaniesPage';
import SitesPage from './pages/SitesPage';
import UsersPage from './pages/UsersPage';
import PermissionsPage from './pages/PermissionsPage';
import SuppliersPage from './pages/SuppliersPage';
import CategoriesPage from './pages/CategoriesPage';
import ProductsPage from './pages/ProductsPage';
import MachinesPage from './pages/MachinesPage';
import ImportsPage from './pages/ImportsPage';
import ApprovalsPage from './pages/ApprovalsPage';
import AuditLogPage from './pages/AuditLogPage';
import AnalyticsOverviewPage from './pages/AnalyticsOverviewPage';
import ProductComparisonPage from './pages/ProductComparisonPage';
import SupplierComparisonPage from './pages/SupplierComparisonPage';
import SavingsPage from './pages/SavingsPage';
import DataQualityPage from './pages/DataQualityPage';
import ReportsPage from './pages/ReportsPage';
import StrataComparisonPage from './pages/StrataComparisonPage';
import LocationsPage from './pages/LocationsPage';
import ChangeOutPage from './pages/ChangeOutPage';
import CloseActiveCyclePage from './pages/CloseActiveCyclePage';
import MachineHistoryPage from './pages/MachineHistoryPage';
import SavingsAgentPage from './pages/SavingsAgentPage';
import DigestPage from './pages/DigestPage';
import SiteAnalyticsPage from './pages/SiteAnalyticsPage';
import SiteDetailPage from './pages/SiteDetailPage';
import MachineAnalyticsPage from './pages/MachineAnalyticsPage';
import MachineDetailPage from './pages/MachineDetailPage';
import OnboardingWizard from './pages/OnboardingWizard';
import FleetStatusDashboard from './pages/FleetStatusDashboard';
import '@/App.css';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen bg-slate-50"><div className="animate-pulse text-slate-400 text-sm">Loading...</div></div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

// Persistent watcher: re-fetches the current user's permissions on every route
// change so sidebar rights update live without requiring re-login.
function RouteWatcher() {
  const { user, refreshUser } = useAuth();
  const location = useLocation();
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    if (user) refreshUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  return null;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <RouteWatcher />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/companies" element={<ProtectedRoute><CompaniesPage /></ProtectedRoute>} />
          <Route path="/sites" element={<ProtectedRoute><SitesPage /></ProtectedRoute>} />
          <Route path="/users" element={<ProtectedRoute><UsersPage /></ProtectedRoute>} />
          <Route path="/permissions" element={<ProtectedRoute><PermissionsPage /></ProtectedRoute>} />
          <Route path="/suppliers" element={<ProtectedRoute><SuppliersPage /></ProtectedRoute>} />
          <Route path="/categories" element={<ProtectedRoute><CategoriesPage /></ProtectedRoute>} />
          <Route path="/products" element={<ProtectedRoute><ProductsPage /></ProtectedRoute>} />
          <Route path="/machines" element={<ProtectedRoute><MachinesPage /></ProtectedRoute>} />
          <Route path="/imports" element={<ProtectedRoute><ImportsPage /></ProtectedRoute>} />
          <Route path="/operations" element={<Navigate to="/change-out" replace />} />
          <Route path="/approvals" element={<ProtectedRoute><ApprovalsPage /></ProtectedRoute>} />
          <Route path="/audit" element={<ProtectedRoute><AuditLogPage /></ProtectedRoute>} />
          <Route path="/analytics" element={<ProtectedRoute><AnalyticsOverviewPage /></ProtectedRoute>} />
          <Route path="/analytics/products" element={<ProtectedRoute><ProductComparisonPage /></ProtectedRoute>} />
          <Route path="/analytics/suppliers" element={<ProtectedRoute><SupplierComparisonPage /></ProtectedRoute>} />
          <Route path="/analytics/savings" element={<ProtectedRoute><SavingsPage /></ProtectedRoute>} />
          <Route path="/analytics/data-quality" element={<ProtectedRoute><DataQualityPage /></ProtectedRoute>} />
          <Route path="/analytics/strata" element={<ProtectedRoute><StrataComparisonPage /></ProtectedRoute>} />
          <Route path="/analytics/sites" element={<ProtectedRoute><SiteAnalyticsPage /></ProtectedRoute>} />
          <Route path="/analytics/sites/:siteId" element={<ProtectedRoute><SiteDetailPage /></ProtectedRoute>} />
          <Route path="/analytics/machines" element={<ProtectedRoute><MachineAnalyticsPage /></ProtectedRoute>} />
          <Route path="/analytics/machines/:machineId" element={<ProtectedRoute><MachineDetailPage /></ProtectedRoute>} />
          <Route path="/analytics/machines/:machineId/history" element={<ProtectedRoute><MachineHistoryPage /></ProtectedRoute>} />
          <Route path="/analytics/savings-agent" element={<ProtectedRoute><SavingsAgentPage /></ProtectedRoute>} />
          <Route path="/locations" element={<ProtectedRoute><LocationsPage /></ProtectedRoute>} />
          <Route path="/change-out" element={<ProtectedRoute><ChangeOutPage /></ProtectedRoute>} />
          <Route path="/close-cycle" element={<ProtectedRoute><CloseActiveCyclePage /></ProtectedRoute>} />
          <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
          <Route path="/digest" element={<ProtectedRoute><DigestPage /></ProtectedRoute>} />
          <Route path="/onboarding" element={<ProtectedRoute><OnboardingWizard /></ProtectedRoute>} />
          <Route path="/fleet" element={<ProtectedRoute><FleetStatusDashboard /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
